import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode
} from 'react'
import { maintenanceCacheEvents, MaintenanceCacheInvalidationEvent } from '../services/api'
import { cacheDebugLogger } from '../utils/cacheDebugLogger'
import { useAuth } from './useAuth'

type MaintenanceCacheEventListener = (event: MaintenanceCacheInvalidationEvent) => void

interface MaintenanceCacheContextValue {
  subscribe(listener: MaintenanceCacheEventListener): () => void
  flushPending(storeIdOverride?: string): void
}

const MaintenanceCacheContext = createContext<MaintenanceCacheContextValue | undefined>(undefined)

interface MaintenanceCacheProviderProps {
  children: ReactNode
}

export function MaintenanceCacheProvider({ children }: MaintenanceCacheProviderProps) {
  const { user, isAuthenticated } = useAuth()
  const listenersRef = useRef<Set<MaintenanceCacheEventListener>>(new Set())
  const storeIdRef = useRef<string | undefined>()

  useEffect(() => {
    storeIdRef.current = user?.store?.trim().toLowerCase() || undefined
  }, [user?.store])

  const notifyListeners = useCallback((event: MaintenanceCacheInvalidationEvent) => {
    if (!event) {
      return
    }

    cacheDebugLogger.debug(
      'MaintenanceCacheProvider.notifyListeners',
      'Propagando evento de invalidação de manutenção',
      {
        reason: event.reason,
        requestId: event.requestId,
        storeId: event.storeId,
        listenerCount: listenersRef.current.size
      }
    )

    listenersRef.current.forEach(listener => {
      try {
        listener(event)
      } catch (error) {
        console.error('[MaintenanceCacheProvider] Listener execution failed', error, {
          reason: event.reason,
          requestId: event.requestId,
          storeId: event.storeId
        })
      }
    })
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      cacheDebugLogger.debug(
        'MaintenanceCacheProvider.effect',
        'Listener global desativado (usuário não autenticado)',
        {}
      )
      return
    }

    cacheDebugLogger.debug(
      'MaintenanceCacheProvider.effect',
      'Registrando listener global de invalidação de cache',
      {
        storeId: user?.store
      }
    )

    const pendingEvents = maintenanceCacheEvents.consumePending(user?.store)
    pendingEvents.forEach(event => {
      cacheDebugLogger.debug(
        'MaintenanceCacheProvider.effect',
        'Processando evento pendente no momento do subscribe global',
        {
          reason: event.reason,
          requestId: event.requestId,
          storeId: event.storeId
        }
      )
      notifyListeners(event)
    })

    const unsubscribe = maintenanceCacheEvents.subscribe(event => {
      const currentStore = storeIdRef.current
      const eventStore = event.storeId?.toLowerCase()

      if (eventStore && currentStore && eventStore !== currentStore) {
        cacheDebugLogger.debug(
          'MaintenanceCacheProvider.effect',
          'Evento ignorado devido a mismatch de loja',
          {
            reason: event.reason,
            requestId: event.requestId,
            eventStore,
            currentStore
          }
        )
        return
      }

      notifyListeners(event)
    })

    return () => {
      cacheDebugLogger.debug(
        'MaintenanceCacheProvider.effect',
        'Desregistrando listener global de invalidação de cache',
        {}
      )
      unsubscribe()
    }
  }, [isAuthenticated, notifyListeners, user?.store])

  const subscribe = useCallback((listener: MaintenanceCacheEventListener) => {
    listenersRef.current.add(listener)

    cacheDebugLogger.debug(
      'MaintenanceCacheProvider.subscribe',
      'Novo listener registrado',
      {
        listenerCount: listenersRef.current.size
      }
    )

    return () => {
      listenersRef.current.delete(listener)
      cacheDebugLogger.debug(
        'MaintenanceCacheProvider.subscribe',
        'Listener removido',
        {
          listenerCount: listenersRef.current.size
        }
      )
    }
  }, [])

  const flushPending = useCallback(
    (storeIdOverride?: string) => {
      const effectiveStoreId = storeIdOverride?.trim().toLowerCase() || user?.store

      const pendingEvents = maintenanceCacheEvents.consumePending(effectiveStoreId)

      cacheDebugLogger.debug(
        'MaintenanceCacheProvider.flushPending',
        'Consumindo eventos pendentes sob demanda',
        {
          requestedStoreId: storeIdOverride,
          effectiveStoreId,
          count: pendingEvents.length
        }
      )

      pendingEvents.forEach(notifyListeners)
    },
    [notifyListeners, user?.store]
  )

  const contextValue = useMemo<MaintenanceCacheContextValue>(
    () => ({
      subscribe,
      flushPending
    }),
    [flushPending, subscribe]
  )

  return (
    <MaintenanceCacheContext.Provider value={contextValue}>
      {children}
    </MaintenanceCacheContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider by design
export function useMaintenanceCacheBus(): MaintenanceCacheContextValue {
  const context = useContext(MaintenanceCacheContext)

  if (!context) {
    throw new Error('useMaintenanceCacheBus deve ser utilizado dentro de MaintenanceCacheProvider')
  }

  return context
}